export class ImageUtils {
    public static readonly WOOPIC_URL = 'https://proxymedia.woopic.com/api/v1/images/';
    public static readonly QUESTION_MARK = '?';
    public static readonly BO_INTERNET_REGEX = /bo\.pushinternet\./;

    /**
     * Replace "$(FORMAT_NAME)" and "CROP VALUE" by correct size in proxymedia URL
     * USECASE :
     * /images/Community.PNG
     * /images/Community.PNG?params=values
     * /images/Community.PNG?format=$(FORMAT_NAME)x
     * /images/Community.PNG?quality=100&crop=$(cropvalue)'
     * 'https://proxymedia.woopic.com/images/Community.PNG?format=$(FORMAT_NAME)x'
     * 'https://proxymedia.woopic.com/images/Community.PNG?quality=100&crop=$(cropvalue)'
     * 'https://current.bo.pushinternet.rec.orange.fr/index.php?menuaction=rulesdresser.ui_media.display_media&id=19694'
     * 'https://example.com/images/image.jpg'
     * @param {string} imgUrl
     * @param {string} size
     * @param {string} cropValue
     * @returns {string}
     */
    public static transformUrl(imgUrl: string, size?: string, cropValue?: string): string {
        if (!imgUrl) {
            return '';
        }

        if (size && cropValue) {
            return '';
        }

        if (ImageUtils.BO_INTERNET_REGEX.test(imgUrl)) {
            imgUrl = decodeURIComponent(imgUrl);
        }

        const parts = imgUrl.replaceAll('%2F', '/').split(ImageUtils.QUESTION_MARK);
        const baseUrl = parts[0];
        const queryParams = parts.length > 1 ? parts[1] : '';

        const imgParts = this.buildQueryParams(queryParams, size, cropValue);

        return this.constructFinalUrl(baseUrl, imgParts, imgUrl);
    }

    /**
     * Replace "$(FORMAT_NAME)" placeholder by the correct size in a proxymedia URL.
     * Variant of transformUrl() dedicated to NDE-format URLs (e.g. ?format=$(FORMAT_NAME)x).
     * @param {string} imgUrl
     * @param {string} size Target size (e.g. '1472x981')
     * @returns {string}
     */
    public static transformUrlForNde(imgUrl: string, size: string): string {
        if (!imgUrl || !size) {
            return imgUrl ?? '';
        }

        if (ImageUtils.BO_INTERNET_REGEX.test(imgUrl)) {
            imgUrl = decodeURIComponent(imgUrl);
        }

        const parts = imgUrl.replaceAll('%2F', '/').split(ImageUtils.QUESTION_MARK);
        const baseUrl = parts[0];
        const queryParams = parts.length > 1 ? parts[1] : '';

        const replacedQuery = queryParams.replace('$(FORMAT_NAME)', size);
        const queryPart = replacedQuery ? ImageUtils.QUESTION_MARK.concat(replacedQuery) : '';

        return this.constructFinalUrl(baseUrl, queryPart, imgUrl);
    }

    /**
     * Build query parameters based on size and cropValue
     * @param queryParams Original query parameters
     * @param size Size to replace FORMAT_NAME with
     * @param cropValue Crop value to use
     * @returns Constructed query parameters
     */
    private static buildQueryParams(queryParams: string, size?: string, cropValue?: string): string {
        if (cropValue) {
            return ImageUtils.QUESTION_MARK.concat(`quality=100&crop=${cropValue}`);
        }

        if (size && queryParams) {
            return ImageUtils.QUESTION_MARK.concat(queryParams.replace('$(FORMAT_NAME)', size));
        }

        return queryParams ? ImageUtils.QUESTION_MARK.concat(queryParams) : '';
    }

    /**
     * Construct the final URL based on input parameters and URL type
     * @param baseUrl Base URL without query parameters
     * @param queryPart Query parameters part
     * @param originalUrl Original URL for type checking
     * @returns Final constructed URL
     */
    private static constructFinalUrl(baseUrl: string, queryPart: string, originalUrl: string): string {
        const hasQueryParams = queryPart.includes(ImageUtils.QUESTION_MARK);

        // Handle BO Internet URLs
        if (ImageUtils.BO_INTERNET_REGEX.test(originalUrl)) {
            return hasQueryParams ? baseUrl.concat(queryPart) : baseUrl;
        }

        // Handle Woopic URLs
        const woopicParts = baseUrl.split(ImageUtils.WOOPIC_URL);
        if (woopicParts.length > 1) {
            if (hasQueryParams) {
                return ImageUtils.WOOPIC_URL.concat(encodeURIComponent(woopicParts[1]), encodeURIComponent(queryPart));
            }
            return ImageUtils.WOOPIC_URL.concat(encodeURIComponent(woopicParts[1]));
        }

        // Handle all other URLs
        return hasQueryParams ? baseUrl.concat(encodeURIComponent(queryPart)) : baseUrl;
    }
}
